import { useCallback, useEffect, useState } from "react";
import { btnGhost, btnPrimary, errMsg } from "../../components/admin/promotionsUi";
import { subscriptionsService } from "../../features/subscriptions/service";
import type { AuditRow, Paged } from "../../features/gamification/types";


// ── Shared bits ──────────────────────────────────────────────────────────────
export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-3 py-2" role="alert">
      <p className="text-sm text-red-400">{message}</p>
      <button onClick={onRetry} className={btnPrimary}>Retry</button>
    </div>
  );
}

export const thCls = "pb-2 pr-3 font-medium";
export const tdCls = "py-2 pr-3";

/** A person as "Name (email)" with the phone under it; the raw id is in the tooltip. A user that is gone shows the id itself. */
export function PersonCell({ id, name, email, phone, fallback }: {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  /** Shown when there is no id and no person (for example "system"). */
  fallback?: string;
}) {
  const known = !!(name || email);
  const main = name && email ? `${name} (${email})` : name || email || id || fallback || "—";
  return (
    <span className="block min-w-0" title={[id, phone].filter(Boolean).join(" · ") || undefined}>
      <span className={`block break-words ${known ? "" : "font-mono text-xs"}`}>{main}</span>
      {known && phone && <span className="block text-xs text-gray-500">{phone}</span>}
    </span>
  );
}

export function Pager<T>({ data, onPage, busy }: { data: Paged<T>; onPage: (p: number) => void; busy: boolean }) {
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-400">
      <button className={btnGhost} disabled={busy || data.page <= 1} onClick={() => onPage(data.page - 1)}>Previous</button>
      <span>Page {data.page} of {pages}{data.total_capped ? "+" : ""} · {data.total}{data.total_capped ? "+" : ""} rows</span>
      <button className={btnGhost} disabled={busy || data.page >= pages} onClick={() => onPage(data.page + 1)}>Next</button>
    </div>
  );
}

const STATUS_TONE: Record<string, string> = {
  granted: "bg-emerald-900/40 text-emerald-300",
  pending: "bg-amber-900/40 text-amber-300",
  reversed: "bg-red-900/40 text-red-300",
  taken_back: "bg-red-900/40 text-red-300",
  expired: "bg-gray-800 text-gray-400",
  active: "bg-emerald-900/40 text-emerald-300",
  expiring: "bg-amber-900/40 text-amber-300",
};

export function StatusBadge({ status }: { status: string | null | undefined }) {
  const s = status || "—";
  return <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_TONE[s] ?? "bg-gray-800 text-gray-300"}`}>{s.replace(/_/g, " ")}</span>;
}

/** The credit rate turns the wallet's paise into credits; null while unknown (amounts then show in rupees). */
export function useCreditRate(): number | null {
  const [rate, setRate] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    subscriptionsService.adminGetCreditRate().then((r) => { if (live && r?.rate_paise > 0) setRate(r.rate_paise); }).catch(() => {});
    return () => { live = false; };
  }, []);
  return rate;
}

export function fmtPaise(paise: number | null | undefined, rate: number | null): string {
  if (paise === null || paise === undefined) return "—";
  return rate ? `${Math.round((paise / rate) * 100) / 100} credits` : `₹${(paise / 100).toFixed(2)}`;
}

export const dash = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : String(v));


/** Header cell with a small (i) icon; the text is a native tooltip and an aria description. */
export function InfoTh({ label, tip, last, center }: { label: string; tip: string; last?: boolean; center?: boolean }) {
  return (
    <th className={`${last ? "pb-2 font-medium" : thCls}${center ? " text-center" : ""}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        <span
          role="img"
          tabIndex={0}
          title={tip}
          aria-label={`${label}: ${tip}`}
          className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-gray-600 text-[9px] leading-none text-gray-400 cursor-help normal-case"
        >
          i
        </span>
      </span>
    </th>
  );
}


export function usePaged<T = AuditRow>(fetcher: (page: number) => Promise<Paged<T>>, failMessage: string) {
  const [data, setData] = useState<Paged<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(async (page: number) => {
    setLoading(true);
    try {
      setData(await fetcher(page));
      setError("");
    } catch (e) {
      setData(null);
      setError(errMsg(e, failMessage));
    } finally {
      setLoading(false);
    }
  }, [fetcher, failMessage]);
  return { data, loading, error, load };
}
